// ═══════════════════════════════════════════════════════════════
// ANALYTICS SERVICE — Real Supabase queries
// ═══════════════════════════════════════════════════════════════
// Fuente de verdad: radio_daily_metrics (agregación autorizada).
// NO usa listener_sessions para agregación (RLS lo impide).
// NO escribe métricas — solo lectura.
// ═══════════════════════════════════════════════════════════════

import { getSupabaseClient } from '@/services/supabase/client';
import type {
  AnalyticsPeriod,
  DateRange,
  RadioAnalytics,
  AggregatedMetrics,
  FollowerMetrics,
  ContentMetrics,
  EngagementMetrics,
  StationMetrics,
  DailyMetricPoint,
  TopContentItem,
  AnalyticsRadioContext,
} from '@/types/analytics';
import { periodToDateRange } from '@/types/analytics';

const supabase = () => getSupabaseClient();

// ═══════════════════════════════════════════════════════════════
// RADIO CONTEXT
// ═══════════════════════════════════════════════════════════════

/** Obtiene las radios que el usuario actual administra */
export async function getMyAnalyticsRadios(): Promise<AnalyticsRadioContext[]> {
  const { data: memberships, error } = await supabase()
    .from('radio_memberships')
    .select('radio_id, radios:radio_id (id, name, slug, logo_url)')
    .eq('is_active', true);

  if (error || !memberships) return [];

  const seen = new Set<string>();
  const radios: AnalyticsRadioContext[] = [];

  for (const m of memberships) {
    const r = Array.isArray(m.radios) ? m.radios[0] : m.radios;
    if (!r || seen.has(r.id)) continue;
    seen.add(r.id);
    radios.push({
      radioId: r.id,
      radioName: r.name || 'Radio sin nombre',
      radioSlug: r.slug || '',
      logoUrl: r.logo_url || undefined,
    });
  }

  return radios;
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS AGREGADAS (radio_daily_metrics)
// ═══════════════════════════════════════════════════════════════

async function fetchAggregatedMetrics(radioId: string, range: DateRange): Promise<AggregatedMetrics> {
  const { data, error } = await supabase()
    .from('radio_daily_metrics')
    .select('total_listeners, total_sessions, total_listening_seconds, peak_concurrent, avg_session_seconds')
    .eq('radio_id', radioId)
    .gte('date', range.start)
    .lte('date', range.end);

  if (error || !data || data.length === 0) {
    return {
      totalListeners: 0,
      totalSessions: 0,
      totalListeningSeconds: 0,
      avgSessionSeconds: 0,
      peakConcurrent: 0,
    };
  }

  const totalListeners = data.reduce((sum, r) => sum + (r.total_listeners || 0), 0);
  const totalSessions = data.reduce((sum, r) => sum + (r.total_sessions || 0), 0);
  const totalListeningSeconds = data.reduce((sum, r) => sum + (Number(r.total_listening_seconds) || 0), 0);
  const peakConcurrent = data.reduce((max, r) => Math.max(max, r.peak_concurrent || 0), 0);
  const avgSessionSeconds = totalSessions > 0
    ? Math.round(totalListeningSeconds / totalSessions)
    : 0;

  return {
    totalListeners,
    totalSessions,
    totalListeningSeconds,
    avgSessionSeconds,
    peakConcurrent,
  };
}

// ═══════════════════════════════════════════════════════════════
// DAILY BREAKDOWN (radio_daily_metrics)
// ═══════════════════════════════════════════════════════════════

async function fetchDailyBreakdown(radioId: string, range: DateRange): Promise<DailyMetricPoint[]> {
  const { data, error } = await supabase()
    .from('radio_daily_metrics')
    .select('date, total_listeners, total_sessions, total_listening_seconds, peak_concurrent')
    .eq('radio_id', radioId)
    .gte('date', range.start)
    .lte('date', range.end)
    .order('date', { ascending: true });

  if (error || !data) return [];

  return data.map((r) => ({
    date: r.date,
    listeners: r.total_listeners || 0,
    sessions: r.total_sessions || 0,
    listeningSeconds: Number(r.total_listening_seconds) || 0,
    peakConcurrent: r.peak_concurrent || 0,
  }));
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS DE SEGUIDORES (radio_followers)
// ═══════════════════════════════════════════════════════════════

async function fetchFollowerMetrics(radioId: string, range: DateRange): Promise<FollowerMetrics> {
  // Total seguidores activos (unfollowed_at IS NULL)
  const { count: totalCount, error: totalError } = await supabase()
    .from('radio_followers')
    .select('*', { count: 'exact', head: true })
    .eq('radio_id', radioId)
    .is('unfollowed_at', null);

  // Nuevos seguidores en el rango
  const { count: newCount, error: newError } = await supabase()
    .from('radio_followers')
    .select('*', { count: 'exact', head: true })
    .eq('radio_id', radioId)
    .gte('followed_at', `${range.start}T00:00:00Z`)
    .lte('followed_at', `${range.end}T23:59:59Z`);

  return {
    totalFollowers: totalError ? 0 : (totalCount || 0),
    newFollowers: newError ? 0 : (newCount || 0),
  };
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS DE CONTENIDO (programs, podcasts, podcast_episodes)
// ═══════════════════════════════════════════════════════════════

async function fetchContentMetrics(radioId: string): Promise<ContentMetrics> {
  const [programsResult, podcastsResult, episodesResult] = await Promise.all([
    supabase().from('programs').select('id, is_active').eq('radio_id', radioId),
    supabase().from('podcasts').select('id').eq('radio_id', radioId),
    supabase()
      .from('podcast_episodes')
      .select('id')
      .in('podcast_id', await (async () => {
        const { data: pods } = await supabase().from('podcasts').select('id').eq('radio_id', radioId);
        return (pods || []).map((p) => p.id);
      })()),
  ]);

  // Re-query episodes simplificado: count desde podcasts
  const { data: pods } = podcastsResult;
  const podcastIds = (pods || []).map((p) => p.id);

  let totalEpisodes = 0;
  if (podcastIds.length > 0) {
    const { count } = await supabase()
      .from('podcast_episodes')
      .select('*', { count: 'exact', head: true })
      .in('podcast_id', podcastIds);
    totalEpisodes = count || 0;
  }

  const programs = programsResult.data || [];
  const totalPrograms = programs.length;
  const activePrograms = programs.filter((p) => p.is_active).length;

  return {
    totalPrograms,
    activePrograms,
    totalPodcasts: (pods || []).length,
    totalEpisodes,
  };
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS DE ENGAGEMENT (chat_messages via chat_rooms)
// ═══════════════════════════════════════════════════════════════

async function fetchEngagementMetrics(radioId: string, range: DateRange): Promise<EngagementMetrics> {
  // Obtener los rooms de esta radio
  const { data: rooms, error: roomsError } = await supabase()
    .from('chat_rooms')
    .select('id')
    .eq('radio_id', radioId);

  if (roomsError || !rooms || rooms.length === 0) {
    return { totalChatMessages: 0, totalSongRequests: 0, totalGreetings: 0 };
  }

  const roomIds = rooms.map((r) => r.id);

  // Total mensajes en el rango
  const { count: totalMsgs, error: msgError } = await supabase()
    .from('chat_messages')
    .select('*', { count: 'exact', head: true })
    .in('room_id', roomIds)
    .gte('created_at', `${range.start}T00:00:00Z`)
    .lte('created_at', `${range.end}T23:59:59Z`);

  // Solicitudes de canciones (mensajes con song_title no nulo o message_type = 'song_request')
  const { count: songReqs, error: songError } = await supabase()
    .from('chat_messages')
    .select('*', { count: 'exact', head: true })
    .in('room_id', roomIds)
    .gte('created_at', `${range.start}T00:00:00Z`)
    .lte('created_at', `${range.end}T23:59:59Z`)
    .not('song_title', 'is', null);

  // Saludos (message_type = 'greeting')
  const { count: greetings, error: greetError } = await supabase()
    .from('chat_messages')
    .select('*', { count: 'exact', head: true })
    .in('room_id', roomIds)
    .gte('created_at', `${range.start}T00:00:00Z`)
    .lte('created_at', `${range.end}T23:59:59Z`)
    .eq('message_type', 'greeting');

  return {
    totalChatMessages: msgError ? 0 : (totalMsgs || 0),
    totalSongRequests: songError ? 0 : (songReqs || 0),
    totalGreetings: greetError ? 0 : (greetings || 0),
  };
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS DE ESTACIONES (stations)
// ═══════════════════════════════════════════════════════════════

async function fetchStationMetrics(radioId: string): Promise<StationMetrics> {
  const { data, error } = await supabase()
    .from('stations')
    .select('status')
    .eq('radio_id', radioId);

  if (error || !data) {
    return { totalStations: 0, activeStations: 0, streamAvailability: 0 };
  }

  const total = data.length;
  const active = data.filter((s) => s.status === 'active').length;
  const availability = total > 0 ? Math.round((active / total) * 100) : 0;

  return {
    totalStations: total,
    activeStations: active,
    streamAvailability: availability,
  };
}

// ═══════════════════════════════════════════════════════════════
// TOP CONTENT (programs, podcasts)
// ═══════════════════════════════════════════════════════════════

async function fetchTopPrograms(radioId: string): Promise<TopContentItem[]> {
  // Ordenamos por la metadata o simplemente listamos los programas activos
  const { data, error } = await supabase()
    .from('programs')
    .select('id, name, is_live, metadata')
    .eq('radio_id', radioId)
    .eq('is_active', true)
    .order('name', { ascending: true })
    .limit(10);

  if (error || !data) return [];

  return data.map((p, idx) => ({
    id: p.id,
    name: p.name,
    type: 'program' as const,
    value: idx + 1,
    unit: 'programa',
    subtitle: p.is_live ? 'En vivo' : 'Grabado',
  }));
}

async function fetchTopPodcasts(radioId: string): Promise<TopContentItem[]> {
  const { data: podcasts, error } = await supabase()
    .from('podcasts')
    .select('id, name')
    .eq('radio_id', radioId)
    .eq('is_active', true)
    .order('name', { ascending: true })
    .limit(10);

  if (error || !podcasts) return [];

  return podcasts.map((p, idx) => ({
    id: p.id,
    name: p.name,
    type: 'podcast' as const,
    value: idx + 1,
    unit: 'podcast',
  }));
}

// ═══════════════════════════════════════════════════════════════
// MÉTODO PRINCIPAL — RadioAnalytics completo
// ═══════════════════════════════════════════════════════════════

export async function getRadioAnalytics(
  radioId: string,
  radioName: string,
  period: AnalyticsPeriod,
  customRange?: DateRange,
  timezone?: string,
): Promise<RadioAnalytics> {
  const dateRange = periodToDateRange(period, customRange, timezone);

  const [metrics, dailyBreakdown, followers, content, engagement, stations, topPrograms, topPodcasts] =
    await Promise.all([
      fetchAggregatedMetrics(radioId, dateRange),
      fetchDailyBreakdown(radioId, dateRange),
      fetchFollowerMetrics(radioId, dateRange),
      fetchContentMetrics(radioId),
      fetchEngagementMetrics(radioId, dateRange),
      fetchStationMetrics(radioId),
      fetchTopPrograms(radioId),
      fetchTopPodcasts(radioId),
    ]);

  return {
    radioId,
    radioName,
    period,
    dateRange,
    metrics,
    followers,
    content,
    engagement,
    stations,
    dailyBreakdown,
    topPrograms,
    topPodcasts,
  };
}